import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

/**
 * WerkDropdown — Werksauswahl-Dropdown fuer LagerView und LagerDashboard.
 * Props:
 *   werke         Array<{ werk: string, name: string }>
 *   selectedWerk  string
 *   onWerkChange  (werk: string) => void
 *   loading       boolean
 *   werkOpen      boolean
 *   setWerkOpen   (v: boolean) => void
 *   werkRef       React.RefObject
 */
const WerkDropdown = ({ werke, selectedWerk, onWerkChange, loading, werkOpen, setWerkOpen, werkRef }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const currentWerkName = werke.find((w) => w.werk === selectedWerk)?.name || selectedWerk;

  return (
    <div className="relative" ref={werkRef}>
      <button
        type="button"
        disabled={loading}
        onClick={() => setWerkOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${isDarkMode
          ? 'bg-white/8 border-white/15 text-white/80 hover:bg-white/12'
          : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <span className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('lm.werkLabel')}:</span>
        <span>{loading ? '...' : (currentWerkName || t('lm.noWerk'))}</span>
        {werke.length > 1 && (
          <ChevronDown className={`w-4 h-4 transition-transform ${werkOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
        )}
      </button>

      {werkOpen && werke.length > 1 && (
        <div className={`absolute right-0 top-full mt-1 z-40 min-w-[220px] rounded-xl border shadow-lg overflow-hidden ${isDarkMode
          ? 'bg-gray-900 border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
          : 'bg-white border-slate-200'
        }`}>
          {werke.map((w) => (
            <button
              key={w.werk}
              type="button"
              onClick={() => onWerkChange(w.werk)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-3 ${w.werk === selectedWerk
                ? isDarkMode ? 'bg-brand/20 text-blue-200' : 'bg-brand/8 text-brand'
                : isDarkMode ? 'text-white/80 hover:bg-white/8' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className={`font-mono text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{w.werk}</span>
              <span className="truncate">{w.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default WerkDropdown;
